import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { PaymentModel } from "@redotech/redo-model/coverage";
import { CoverageMonitor as CoverageMonitorType } from "@redotech/redo-model/coverage-monitor";
import { CoverageProductEnum } from "@redotech/redo-model/integration/redo-in-shipping/ris";
import { Onboarding } from "@redotech/redo-model/onboarding";
import { MerchantCarrierReturnLabelMode } from "@redotech/redo-model/returns/merchant-carrier-return-label-schema";

import {
  DEFAULT_PER_RETURN_BILLING_FEE,
  DynamicReturnTypes,
  ReturnLabelQuantity,
  returnsCoverageIsEnabled,
  ShippingInsurance,
  ShopSiteNavigationMode,
  Team,
} from "@redotech/redo-model/team";
import { Settings as AuditableSettings } from "@redotech/redo-model/team-settings-audit";

import {
  isAttachmentStrategyCheckoutExtensionEnabled,
  isAttachmentStrategyUsingCartToggles,
} from "@redotech/redo-model/returns/attachment-strategy";

import * as gridCss from "@redotech/redo-web/grid.module.css";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";

import { pricingRulesConvertValueToTeam } from "@redotech/redo-web/settings-elements/dynamic-pricing";

import {
  finalSaleReturnsDefault,
  finalSaleReturnsForm,
  finalSaleReturnsFormValueToTeam,
  finalSaleReturnsTeamToFormValue,
} from "@redotech/redo-web/settings-elements/final-sale";

import {
  packageProtectionDefault,
  packageProtectionForm,
  packageProtectionFormValueToTeam,
  packageProtectionTeamToFormValue,
} from "@redotech/redo-web/settings-elements/package-protection-elements";

import { groupInput, InputProvider } from "@redotech/ui/form";
import classNames from "classnames";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import round from "lodash/round";

import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../client/context";

import {
  getCoverageMonitoring,
  resetCoverageMonitoring,
} from "../../client/coverage-monitoring";

import { getOnboarding } from "../../client/onboarding";
import { notifySlack, updateCoverage } from "../../client/orders";
import { getTeam, updateTeam } from "../../client/team";
import { PreviewButton } from "../split/preview-button";
import { LoadTeamContext, TeamContext } from "../team";
import * as teamCss from "../team.module.css";

import {
  AdditionalCoverageWebSourcesCard,
  additionalCoverageWebSourcesDefault,
  additionalCoverageWebSourcesForm,
} from "./returns-package-protection/additional-coverage-web-sources";

import { BannerInfo } from "./returns-package-protection/banner-info";

import {
  BillingType,
  billingTypeDefault,
  billingTypeForm,
} from "./returns-package-protection/billing-type/billing-type";

import { CopyLatestReturnFlowCard } from "./returns-package-protection/copy-latest-return-flow";
import { CoverageMonitoringCard } from "./returns-package-protection/coverage-monitor";

import {
  CoverageProduct,
  risDefault,
  risForm,
  RisFormValue,
} from "./returns-package-protection/coverage-product";

import {
  createExchangeAgentBillingSettingsFormValue,
  exchangeAgentBillingConfigSettingsDefault,
  ExchangeAgentBillingSettingsCard,
  exchangeAgentBillingSettingsForm,
  getUpdatedExchangeAgentBillingSettings,
} from "./returns-package-protection/exchange-agent-billing-settings";

import {
  LabelRatesCard,
  returnLabelsDefault,
  returnLabelsForm,
} from "./returns-package-protection/label-rate/label-rates-card";

import { PackageProtectionCoverageCard } from "./returns-package-protection/package-protection/coverage";
import { PackageProtectionGeneralCard } from "./returns-package-protection/package-protection/general";

import {
  PerReturnBillingSettingsCard,
  perReturnBillingSettingsDefault,
  perReturnBillingSettingsForm,
} from "./returns-package-protection/per-return-billing-settings";

import { ShippingZones } from "./returns-package-protection/redo-in-shipping/shipping-zones";

import { ReturnBearMarkupSettings } from "./returns-package-protection/returnbear-markup-settings";

import {
  ReturnProcessingCard,
  returnProcessingDefault,
  returnProcessingForm,
} from "./returns-package-protection/return-processing";

import {
  FinalSaleReturnsCard,
  ReturnsCoverageCard,
  returnsCoverageDefault,
  returnsCoverageForm,
  returnsCoverageTeamToFormValue,
  ReturnsPricingEnum,
} from "./returns-package-protection/returns/coverage";

import {
  ExchangesCard,
  exchangesDefault,
  exchangesForm,
} from "./returns-package-protection/returns/exchanges";

import {
  ReturnsGeneralCard,
  returnsGeneralDefault,
  returnsGeneralForm,
  teamToReturnsGeneralFormValue,
} from "./returns-package-protection/returns/general";

import {
  ShippingCard,
  shippingDefault,
  shippingForm,
} from "./returns-package-protection/shipping";

import {
  StorefrontCard,
  storefrontDefault,
  storefrontForm,
  storefrontTeamToFormValue,
} from "./returns-package-protection/storefront";

import {
  SubscriptionBillingCard,
  subscriptionBillingDefault,
  subscriptionBillingForm,
  subscriptionBillingFormValueToTeam,
  subscriptionBillingSettingsToFormValue,
} from "./common/subscription-billing";

import { getChanges } from "@redotech/redo-model/team-settings-audit";
import { Card } from "@redotech/redo-web/card";
import { TeamChangesTooltip } from "./returns-package-protection/component/team-changes-tooltip";
import {
  yofiDefault,
  yofiForm,
  yofiFormValueToTeam,
  YofiIntegrationCard,
  yofiTeamToFormValue,
} from "./returns-package-protection/yofi-integration";

const defaultReturnsPricingRuleSet: any = {
  type: "percentage",
  countries: [],
  priceBrackets: [{ value: "2", pricePoint: "0" }],
};

export const ReturnsPackageProtectionPage = memo(
  function ReturnsPackageProtectionPage() {
    const client = useRequiredContext(RedoAdminClientContext);
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const params = useParams();
    const teamId = params.teamId!;
    const team = useContext(TeamContext);
    const loadTeam = useRequiredContext(LoadTeamContext);

    const [onboardingLoad, loadOnboarding] = useTriggerLoad((signal) =>
      getOnboarding(client, { teamId, signal }),
    );

    const [risIntegrationLoad, loadRisIntegration] = useTriggerLoad((signal) =>
      rpcClient.getRisIntegration(
        { teamId, integrationType: "Shopify" },
        { signal },
      ),
    );

    const bigcommerceIntegrationLoad = useLoad(
      (signal) => rpcClient.getBigcommerceIntegration({ teamId }, { signal }),
      [rpcClient, teamId],
    );

    //Whether or not this merchant has access to carrier services
    const hasCarrierServices = useLoad(
      async (signal) => {
        if (risIntegrationLoad.value?.hasShopifyIntegration) {
          return (await rpcClient.hasCarrierServices({ teamId }, { signal }))
            .hasCarrierServices;
        }
        return false;
      },
      [rpcClient, risIntegrationLoad.value],
    );

    const [coverageMonitoringLoad, loadCoverageMonitoring] = useTriggerLoad(
      (signal) => getCoverageMonitoring(client, { teamId, signal }),
    );

    const handleSave = useHandler(() => {
      loadTeam();
      loadOnboarding();
      loadCoverageMonitoring();
      loadRisIntegration();
    });

    const value = useMemo(() => {
      if (!team) return undefined;
      if (!risIntegrationLoad.value && !risIntegrationLoad.error)
        return undefined;
      const packageProtectionFormValue = packageProtectionTeamToFormValue(team);
      const finalSaleFormValue = finalSaleReturnsTeamToFormValue(team);
      const yofiFormValue = yofiTeamToFormValue(team);
      const returnsGeneralFormValue = teamToReturnsGeneralFormValue(team);
      const returnsCoverageFormValue = returnsCoverageTeamToFormValue(team);
      const storefrontFormValue = storefrontTeamToFormValue(team);

      // Debug AI Exchange settings initialization
      const risForm: RisFormValue = risIntegrationLoad.value?.risIntegration
        ? {
            ...risIntegrationLoad.value.risIntegration,
            coverage: risIntegrationLoad.value.risIntegration.coverage
              ? risIntegrationLoad.value.risIntegration.coverage
              : CoverageProductEnum.SHIPPING,
            deliveryFilterType:
              risIntegrationLoad.value.risIntegration.deliveryFilterType,
            deliverySortType:
              risIntegrationLoad.value.risIntegration.deliverySortType,
          }
        : risIntegrationLoad.value?.hasShopifyIntegration
          ? risDefault
          : undefined;
      return {
        returnProcessing: {
          shopAgain: team.settings.shopAgainAction,
          riseTriggerUrl: team.settings.riseTrigger?.url || "",
          portalExcludedProductTags:
            team.settings.portalExcludedProductTags || [],
          portalExcludedCollections:
            team.settings.portalExcludedCollections || [],
          portalExcludedProductIds:
            team.settings.portalExcludedProductIds || [],
          hideGiftCardBranding: !!team.settings.returns?.hideGiftCardBranding,
          allowRestockBeforeProcess:
            !!team.settings.inventory?.allowRestockBeforeProcess,
          checkForTryNowTags: !!team.settings.returns?.checkForTryNowTags,
        },
        returnsGeneral: returnsGeneralFormValue,
        returnsCoverage: returnsCoverageFormValue,
        finalSaleReturns: finalSaleFormValue,
        exchanges: {
          selection: team.settings.exchanges.selection,
          checkoutSelector: team.settings.checkoutSelector ?? "",
          exchangeBannerColor: team.settings.exchangeBannerColor || "",
          exchangeBannerPositionBottom:
            team.settings?.shopifyExtension?.exchangeBannerPositionBottom ||
            false,
          exchangeBannerInsertBeforeBody:
            team.settings?.shopifyExtension?.exchangeBannerInsertBeforeBody ||
            false,

          exchangeBannerText: team.settings.exchangeBannerText || "",
          exchangeBannerFont: team.settings.exchangeBannerFont || "",
          exchangeBannerFontColor: team.settings.exchangeBannerFontColor || "",
          exchangeBannerShowDoneButton:
            team.settings.exchangeBannerShowDoneButton ?? true,
          exchangeBannerCustomCss: team.settings.exchangeBannerCustomCss || "",
          exchangeOptionsPreviewShowOriginalPrice:
            team.settings.exchanges.exchangeOptionsPreviewShowOriginalPrice ??
            false,
          shopSiteNewTab:
            team.settings.exchanges.shopSite.navigationMode ===
            ShopSiteNavigationMode.NEW_TAB,
        },
        packageProtection: packageProtectionFormValue,
        yofi: yofiFormValue,
        shipping: {
          carrierOverride: team.settings.shippingCarrierOverride,
          labelAdjustment:
            team.settings.labelPriceAdjustment != null
              ? team.settings.labelPriceAdjustment.toFixed(2)
              : "",
          pickupEnabled: team.settings.pickupEnabled,
          pickupEnabledForHeavyItems:
            team.settings.pickupEnabledForHeavyItems ?? false,
          pickupMaxLabelUpgradeRange:
            team.settings.pickupMaxLabelUpgradeRange?.toString() ?? "",
          enableOnHoldOrders: !!team.settings.exchanges.enableOnHoldOrders,
          onHoldOrdersOnUpsellOnly:
            !!team.settings.exchanges.onHoldOrdersOnUpsellOnly,
          insured:
            team.settings.shippingInsurance === ShippingInsurance.ENABLED,
          canEditReturnAddress: team.settings.canEditReturnAddress ?? false,
          generateQrCodes: team.settings.generateQrCodes,
          returnLabelQuantity:
            team.settings.returns?.returnLabelQuantity ??
            ReturnLabelQuantity.PER_RETURN,
          useFixedPackageDimensions:
            team.settings.returns?.useFixedPackageDimensions ?? false,
          fixedPackageDimensions: {
            length:
              team.settings.returns?.fixedPackageDimensions?.length?.toString() ??
              "",
            width:
              team.settings.returns?.fixedPackageDimensions?.width?.toString() ??
              "",
            height:
              team.settings.returns?.fixedPackageDimensions?.height?.toString() ??
              "",
            weight:
              team.settings.returns?.fixedPackageDimensions?.weight?.toString() ??
              "",
          },
          refundExpiredLabels:
            team.settings.returns?.refundExpiredLabels ?? false,
        },
        billingType: { subscriptionEnabled: team.settings.subscriptionEnabled },
        storefront: storefrontFormValue,
        ris: risForm,
        returnLabels: {
          disableMerchantPaidUpcharge:
            team.settings.returns?.removeMerchantPaidLabelUpcharge ?? false,
          merchantCarrierReturnLabels: team.settings.returns
            ?.merchantCarrierReturnLabels ?? {
            mode: MerchantCarrierReturnLabelMode.ONLY_REDO_CARRIERS,
            whitelistedCountries: [],
          },
        },
        additionalCoverageWebSources:
          team.settings.additionalCoverageWebSources || [],
        exchangeAgentBillingSettings:
          createExchangeAgentBillingSettingsFormValue(
            team.settings.returnsAiExchange,
          ),
        perReturnBillingSettings: {
          enabled: team.settings.perReturnBilling?.enabled ?? false,
          usageFee: (
            team.settings.perReturnBilling?.usageFee ??
            DEFAULT_PER_RETURN_BILLING_FEE
          ).toString(),
        },
        subscriptionBilling: subscriptionBillingSettingsToFormValue(
          team.settings.returns?.subscriptionBilling,
        ),
      };
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [team, risIntegrationLoad.value]);

    const [apiLoading, setApiLoading] = useState(true);
    const isLoading = apiLoading || !value;

    useLayoutEffect(() => {
      const loadAll = async () => {
        setApiLoading(true);
        try {
          await Promise.all([
            loadTeam(),
            loadOnboarding(),
            loadCoverageMonitoring(),
            loadRisIntegration(),
          ]);
        } catch (error) {
          console.error("Failed to load data:", error);
        } finally {
          setApiLoading(false);
        }
      };
      void loadAll();
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamId]);

    if (isLoading) {
      return null;
    }

    return (
      <div className={teamCss.container}>
        <div
          style={{
            backgroundColor: "#ffeb3b",
            border: "2px solid #f57c00",
            borderRadius: "8px",
            padding: "160px 20px",
            marginBottom: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              color: "#d32f2f",
              marginTop: 0,
              fontSize: "24px",
              fontWeight: "bold",
            }}
          >
            ⚠️ This settings page is deprecated and will no longer be available
            after Wednesday, January 7th, 2026.
          </h2>
          <p style={{ fontSize: "16px", marginBottom: "16px", color: "#333" }}>
            Please use the new returns settings to configure your returns and
            package protection.
          </p>
          <a
            href={`/teams/${teamId}/returns/general`}
            style={{
              backgroundColor: "#1976d2",
              color: "white",
              padding: "10px 24px",
              borderRadius: "4px",
              textDecoration: "none",
              fontSize: "16px",
              fontWeight: "500",
              display: "inline-block",
            }}
          >
            Go to New Returns Settings →
          </a>
        </div>
        {bigcommerceIntegrationLoad.value?.hasBigcommerceIntegration && (
          <BannerInfo text="This team has Bigcommerce integration enabled. Some functionality may be limited." />
        )}
        {value && (
          <ReturnsPackageProtectionForm
            coverageMonitor={coverageMonitoringLoad.value}
            hasBigcommerceIntegration={
              bigcommerceIntegrationLoad.value?.hasBigcommerceIntegration ??
              false
            }
            hasCarrierServices={hasCarrierServices.value ?? false}
            hasShopifyIntegration={
              risIntegrationLoad.value?.hasShopifyIntegration === true
            }
            id={teamId}
            onboarding={onboardingLoad.value}
            onSave={handleSave}
            value={value}
          />
        )}
      </div>
    );
  },
);

const returnsPackageProtectionForm = groupInput({
  returnProcessing: returnProcessingForm,
  returnsGeneral: returnsGeneralForm,
  returnsCoverage: returnsCoverageForm,
  exchanges: exchangesForm,
  packageProtection: packageProtectionForm,
  finalSaleReturns: finalSaleReturnsForm,
  yofi: yofiForm,
  shipping: shippingForm,
  billingType: billingTypeForm,
  storefront: storefrontForm,
  ris: risForm,
  returnLabels: returnLabelsForm,
  additionalCoverageWebSources: additionalCoverageWebSourcesForm,
  exchangeAgentBillingSettings: exchangeAgentBillingSettingsForm,
  perReturnBillingSettings: perReturnBillingSettingsForm,
  subscriptionBilling: subscriptionBillingForm,
});

type ReturnsPackageProtectionForm = InputProvider.Form<
  typeof returnsPackageProtectionForm
>;

type ReturnsPackageProtectionValue = InputProvider.Value<
  typeof returnsPackageProtectionForm
>;

const returnsPackageProtectionDefault: ReturnsPackageProtectionValue = {
  returnProcessing: returnProcessingDefault,
  returnsGeneral: returnsGeneralDefault,
  returnsCoverage: returnsCoverageDefault,
  exchanges: exchangesDefault,
  packageProtection: packageProtectionDefault,
  finalSaleReturns: finalSaleReturnsDefault,
  yofi: yofiDefault,
  shipping: shippingDefault,
  billingType: billingTypeDefault,
  storefront: storefrontDefault,
  ris: risDefault,
  returnLabels: returnLabelsDefault,
  additionalCoverageWebSources: additionalCoverageWebSourcesDefault,
  exchangeAgentBillingSettings: exchangeAgentBillingConfigSettingsDefault,
  perReturnBillingSettings: perReturnBillingSettingsDefault,
  subscriptionBilling: subscriptionBillingDefault,
};

const ReturnsPackageProtectionForm = memo(
  function ReturnsPackageProtectionForm({
    onSave,
    id,
    onboarding,
    coverageMonitor,
    value,
    hasShopifyIntegration,
    hasCarrierServices,
    hasBigcommerceIntegration,
  }: {
    id: string;
    value: ReturnsPackageProtectionValue | undefined;
    onboarding: Onboarding | undefined;
    coverageMonitor: CoverageMonitorType | undefined;
    onSave?: (value: ReturnsPackageProtectionValue) => void;
    hasShopifyIntegration: boolean;
    hasCarrierServices: boolean;
    hasBigcommerceIntegration: boolean;
  }) {
    const client = useRequiredContext(RedoAdminClientContext);
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const formInitialValue = value || returnsPackageProtectionDefault;
    const input = useInput(returnsPackageProtectionForm, formInitialValue);

    const {
      returnProcessing,
      returnsGeneral,
      returnsCoverage,
      exchanges,
      packageProtection,
      finalSaleReturns,
      shipping,
      billingType,
      storefront,
      ris,
      returnLabels,
      additionalCoverageWebSources,
      exchangeAgentBillingSettings,
      perReturnBillingSettings,
      subscriptionBilling,
    } = input.inputs;

    useEffect(() => {
      if (!value) {
        return;
      }
      // Overwrite the input value with the value from the server.
      // This is because we're using unique symbols as keys so they'll never be equal.
      if (value.storefront.hideRedoProduct?.cartCountModifications) {
        input.inputs.storefront.inputs.hideRedoProduct.inputs.cartCountModifications.setValue(
          value.storefront.hideRedoProduct.cartCountModifications,
        );
      }
      if (value.storefront.hideRedoProduct?.cartPriceModifications) {
        input.inputs.storefront.inputs.hideRedoProduct.inputs.cartPriceModifications.setValue(
          value.storefront.hideRedoProduct.cartPriceModifications,
        );
      }
      if (value.storefront.hideRedoProduct?.cartDiscountModifications) {
        input.inputs.storefront.inputs.hideRedoProduct.inputs.cartDiscountModifications.setValue(
          value.storefront.hideRedoProduct.cartDiscountModifications,
        );
      }

      if (value.returnsCoverage.internationalPricing) {
        input.inputs.returnsCoverage.inputs.internationalPricing.setValue(
          value.returnsCoverage.internationalPricing,
        );
      }

      if (value.packageProtection.pricingRuleSet) {
        input.inputs.packageProtection.inputs.pricingRuleSet.setValue(
          value.packageProtection.pricingRuleSet,
        );
        if (value.packageProtection.internationalPricingEnabled !== undefined) {
          input.inputs.packageProtection.inputs.internationalPricingEnabled.setValue(
            value.packageProtection.internationalPricingEnabled,
          );
        }
      }

      if (value.exchangeAgentBillingSettings) {
        input.inputs.exchangeAgentBillingSettings.inputs.billingTier.setValue(
          value.exchangeAgentBillingSettings.billingTier,
        );
        input.inputs.exchangeAgentBillingSettings.inputs.enabled.setValue(
          value.exchangeAgentBillingSettings.enabled,
        );
        input.inputs.exchangeAgentBillingSettings.inputs.enabledBilling.setValue(
          value.exchangeAgentBillingSettings.enabledBilling,
        );
        input.inputs.exchangeAgentBillingSettings.inputs.renewOnPeriodEnd.setValue(
          value.exchangeAgentBillingSettings.renewOnPeriodEnd,
        );
        input.inputs.exchangeAgentBillingSettings.inputs.startDate.setValue(
          value.exchangeAgentBillingSettings.startDate,
        );
        input.inputs.exchangeAgentBillingSettings.inputs.usageFee.setValue(
          value.exchangeAgentBillingSettings.usageFee,
        );
      }

      if (value.perReturnBillingSettings) {
        input.inputs.perReturnBillingSettings.inputs.enabled.setValue(
          value.perReturnBillingSettings.enabled,
        );
        input.inputs.perReturnBillingSettings.inputs.usageFee.setValue(
          value.perReturnBillingSettings.usageFee,
        );
      }

      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const prevSplitProducts = useRef(value?.storefront.splitProducts);
    const originalDbStrategy = useRef(value?.storefront.attachmentStrategy);
    const [toggleCount, setToggleCount] = useState(0);

    useEffect(() => {
      prevSplitProducts.current = value?.storefront.splitProducts;
      originalDbStrategy.current = value?.storefront.attachmentStrategy;
    }, [value?.storefront.splitProducts, value?.storefront.attachmentStrategy]);

    useEffect(() => {
      if (!value) return;

      const packageProtectionEnabled = packageProtection.value.enabled;
      const returnsEnabled = returnsGeneral.value.enabled;
      const splitProducts = storefront.value.splitProducts;

      // Handle toggle count first
      if (prevSplitProducts.current !== splitProducts) {
        setToggleCount((prev) => prev + 1);
        prevSplitProducts.current = splitProducts;
      }

      // If either protection or returns is disabled, force split products off
      if ((!packageProtectionEnabled || !returnsEnabled) && splitProducts) {
        input.inputs.storefront.inputs.splitProducts.setValue(false);
        return;
      }

      // Handle product options based on split mode
      if (splitProducts) {
        if (value.storefront.PackageProtectionProductOptions) {
          input.inputs.storefront.inputs.PackageProtectionProductOptions.setValue(
            value.storefront.PackageProtectionProductOptions,
          );
        }
        if (value.storefront.returnProductOptions) {
          input.inputs.storefront.inputs.returnProductOptions.setValue(
            value.storefront.returnProductOptions,
          );
        }
      } else if (value.storefront.BothProductOptions) {
        input.inputs.storefront.inputs.BothProductOptions.setValue(
          value.storefront.BothProductOptions,
        );
      }
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      packageProtection.value.enabled,
      returnsGeneral.value.enabled,
      storefront.value.splitProducts,
      value?.storefront.attachmentStrategy,
      toggleCount,
    ]);

    const save: PageFormSave = useHandler(async (signal) => {
      const team = await getTeam(client, { teamId: id, signal });

      const prevMerchantPaidEnabledDates = cloneDeep(
        team.settings.merchantPaidEnabledDates,
      );

      const prevPackagePickupEnabled = cloneDeep(team.settings.pickupEnabled);

      const formValue = input.value;
      const pricingType = formValue.returnsCoverage.returnPricingSelection;

      const updatedTeam = returnsPackageProtectionFormValueToTeam({
        formValue,
        prevTeamValue: team,
      });

      // I'm shuffling code and don't want to deal with this right now since we're about to scrap this page but this code is evil. Why are we setting inputs on save?
      const dynamicPricingRuleSets = pricingRulesConvertValueToTeam(
        formValue.returnsCoverage.dynamicPriceCartValueRuleSet,
      )?.[0];
      if (
        pricingType === ReturnsPricingEnum.PER_ITEM &&
        formValue.returnsCoverage.pricePerAdditonalItem &&
        formValue.returnsCoverage.maxPrice
      ) {
        input.inputs.returnsCoverage.inputs.dynamicPriceCartValueRuleSet.setValue(
          [defaultReturnsPricingRuleSet],
        );
      } else if (
        pricingType === ReturnsPricingEnum.ORDER_TOTAL &&
        dynamicPricingRuleSets
      ) {
        input.inputs.returnsCoverage.inputs.pricePerAdditonalItem.setValue(
          formValue.returnsCoverage.pricePerOrder || "2",
        );
      } else {
        input.inputs.returnsCoverage.inputs.maxPrice.setValue("5");
        input.inputs.returnsCoverage.inputs.pricePerAdditonalItem.setValue(
          formValue.returnsCoverage.pricePerOrder || "2",
        );
        input.inputs.returnsCoverage.inputs.dynamicPriceCartValueRuleSet.setValue(
          [defaultReturnsPricingRuleSet],
        );
      }
      if (
        !formValue.returnsGeneral.enabled ||
        !formValue.packageProtection.enabled
      ) {
        input.inputs.storefront.inputs.splitProducts.setValue(false);
      }

      if (team.settings.packageProtection?.percentage) {
        input.inputs.packageProtection.inputs.percentage.setValue(
          String(round(team.settings.packageProtection.percentage * 100.0, 3)),
        );
      }

      const newMerchantPaidEnabledDates =
        formValue.returnsCoverage.merchantPaidEnabledDates.map((value) => ({
          start: value.start?.toISOString() || null,
          end: value.end?.toISOString() || null,
        }));

      await updateTeam(client, { teamId: id, team: updatedTeam, signal });

      //Ris setup
      if (hasShopifyIntegration) {
        let risIntegration = (
          await rpcClient.getRisIntegration(
            { teamId: id, integrationType: "Shopify" },
            { signal },
          )
        ).risIntegration;

        if (!isEqual(formValue.ris, risIntegration)) {
          //If the new shipping coverage is not defined, but it is defined on backend, just disable it so they won't lose their changes.
          if (!formValue.ris && risIntegration) {
            risIntegration.enabled = false;
          } else {
            risIntegration = {
              ...formValue.ris!,
              enabled:
                formValue.ris?.coverage === CoverageProductEnum.SHIPPING &&
                formValue.ris?.enabled === true,
            };
          }

          //If we enable/disable package protection or returns coverage we must update the RIS integration

          for (const rateTable of risIntegration.rateTables) {
            rateTable.coverageTypes = {
              packageProtection:
                formValue.packageProtection.enabled &&
                formValue.packageProtection.coverage,
              return: returnsCoverageIsEnabled(
                formValue.returnsCoverage.coverage,
                formValue.returnsGeneral.enabled,
              ),
            };

            for (const rate of rateTable.rates) {
              if (rate.merchantPaidCoverageTypes) {
                if (!formValue.packageProtection.enabled) {
                  rate.merchantPaidCoverageTypes.packageProtection = undefined;
                }
                if (!formValue.returnsGeneral.enabled) {
                  rate.merchantPaidCoverageTypes.return = undefined;
                }
              }
            }
          }
          await rpcClient.updateRisIntegration(
            {
              teamId: id,
              integrationType: "Shopify",
              integration: risIntegration,
            },
            { signal },
          );
        }
      }

      if (!isEqual(prevMerchantPaidEnabledDates, newMerchantPaidEnabledDates)) {
        await updateCoverage(client, { teamId: id, signal });
      }
      if (prevPackagePickupEnabled && !formValue.shipping.pickupEnabled) {
        await notifySlack(client, { teamId: id, teamName: team.name, signal });
      }

      onSave?.(formValue);
    });

    // why in the world are we setting things here when we're already passing the input object to the various settings components?
    const setBannerColor = useHandler((color: string) => {
      input.inputs.exchanges.inputs.exchangeBannerColor.setValue(color);
    });

    const setBannerFontColor = useHandler((color: string) => {
      input.inputs.exchanges.inputs.exchangeBannerFontColor.setValue(color);
    });

    const setToggleOnColor = useHandler((color: string) => {
      input.inputs.storefront.inputs.cartToggle.inputs.toggleOnColor.setValue(
        color,
      );
    });

    const setToggleOffColor = useHandler((color: string) => {
      input.inputs.storefront.inputs.cartToggle.inputs.toggleOffColor.setValue(
        color,
      );
    });

    const setToggleCircleColor = useHandler((color: string) => {
      input.inputs.storefront.inputs.cartToggle.inputs.toggleCircleColor.setValue(
        color,
      );
    });

    const getTreatment = useHandler(async () => {
      const currentTeam = await getTeam(client, { teamId: id });
      const value = input.value;
      return {
        name: "temp",
        weight: 1,
        active: false,
        settings: {
          redoEnabled: true,
          returnEnabled: value.returnsGeneral.enabled,
          packageProtectionEnabled: value.packageProtection.enabled,
          attachmentStrategy: value.storefront.attachmentStrategy,
          toggleTextOptions: {
            returnToggle: {
              titleText: value.storefront.returnProductOptions.toggleMainText,
              positiveSubtext:
                value.storefront.returnProductOptions.toggleSubTextPositive,
              negativeSubtext:
                value.storefront.returnProductOptions.toggleSubTextNegative,
            },
            packageProtectionToggle: {
              titleText:
                value.storefront.PackageProtectionProductOptions.toggleMainText,
              positiveSubtext:
                value.storefront.PackageProtectionProductOptions
                  .toggleSubTextPositive ?? "",
              negativeSubtext:
                value.storefront.PackageProtectionProductOptions
                  .toggleSubTextNegative ?? "",
            },
            bothProductToggle: {
              titleText:
                value.storefront.BothProductOptions.toggleMainText ?? "",
              positiveSubtext:
                value.storefront.BothProductOptions.toggleSubTextPositive ?? "",
              negativeSubtext:
                value.storefront.BothProductOptions.toggleSubTextNegative ?? "",
            },
          },
          hideBranding: currentTeam.settings.hidePortalBranding,
          cartElementPlacementSelector: value.storefront.cartToggle.selector,
          cartListenerSelector: value.storefront.cartToggle.clickSelector,
          cartElementPlacement: value.storefront.cartToggle.placement,
          hideCartProduct: value.storefront.hideRedoProduct.enabled,
          cartElementImage: undefined, //@Samuel TODO this should be a field on the admin dash
          redoAutoEnable: value.storefront.checkbox.default,
          packageProtectionAutoEnable:
            value.storefront.packageProtectionAutoCheck,
          hideOnPDP: true, //@Samuel TODO this should be a field on the admin dash
          usingCartAndCheckoutToggle: undefined, //@Samuel TODO this should be a field on the admin dash
          customToggleCss: value.storefront.customToggleCss,
        },
      };
    });

    const [pendingChangesTooltip, loadPendingChangesTooltip] = useTriggerLoad(
      async (signal: AbortSignal) => {
        if (!input.changed) {
          return undefined;
        }
        const team = await getTeam(client, { teamId: id, signal });

        const newTeam = returnsPackageProtectionFormValueToTeam({
          prevTeamValue: team,
          formValue: input.value,
        });

        const pendingChanges = getChanges({
          prev: team.settings as unknown as AuditableSettings,
          next: newTeam.settings as unknown as AuditableSettings,
        });
        return <TeamChangesTooltip changes={pendingChanges} />;
      },
    );

    useEffect(() => {
      loadPendingChangesTooltip();
    }, [loadPendingChangesTooltip, input.value]);

    return (
      <div className={teamCss.container}>
        {value && (
          <PageForm
            additionalActions={
              <PreviewButton getTreatment={getTreatment} teamId={id} />
            }
            initial={value}
            input={input}
            save={save}
            saveButtonHoverContentSlot={pendingChangesTooltip.value}
          >
            <div className={classNames(teamCss.mainForm, gridCss.grid)}>
              <BillingType
                billingType={billingType}
                returnCoverage={returnsCoverage}
              />
              <div className={classNames(gridCss.span12, gridCss.span6L)}>
                <div className={gridCss.grid}>
                  <div className={gridCss.span12}>
                    <ReturnsGeneralCard input={returnsGeneral} />
                  </div>
                  {returnsGeneral.value.enabled &&
                    !billingType.value.subscriptionEnabled && (
                      <div className={gridCss.span12}>
                        <Card title="Returns Coverage">
                          <ReturnsCoverageCard
                            input={returnsCoverage}
                            onboarding={onboarding}
                          />
                        </Card>
                      </div>
                    )}
                  {returnsGeneral.value.enabled && (
                    <div className={gridCss.span12}>
                      <ExchangesCard
                        input={exchanges}
                        setBannerColor={setBannerColor}
                        setBannerFontColor={setBannerFontColor}
                      />
                    </div>
                  )}
                  <div className={gridCss.span12}>
                    <Card title="Package Protection">
                      <PackageProtectionGeneralCard input={packageProtection} />
                    </Card>
                  </div>
                  {!billingType.value.subscriptionEnabled && (
                    <div className={gridCss.span12}>
                      <StorefrontCard
                        hasBigcommerceIntegration={hasBigcommerceIntegration}
                        input={storefront}
                        packageProtection={packageProtection.value}
                        returnsCoverage={returnsCoverage.value}
                        returnsGeneral={returnsGeneral.value}
                        setToggleCircleColor={setToggleCircleColor}
                        setToggleOffColor={setToggleOffColor}
                        setToggleOnColor={setToggleOnColor}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className={classNames(gridCss.span12, gridCss.span6L)}>
                <div className={gridCss.grid}>
                  {hasShopifyIntegration &&
                    !billingType.value.subscriptionEnabled && (
                      <div className={gridCss.span12}>
                        <CoverageProduct
                          input={ris}
                          storefrontForm={storefront}
                        />
                      </div>
                    )}
                  {packageProtection.value.enabled && (
                    <div className={gridCss.span12}>
                      <Card title="Package Protection Coverage">
                        <PackageProtectionCoverageCard
                          input={packageProtection}
                        />
                      </Card>
                    </div>
                  )}
                  <div className={gridCss.span12}>
                    <Card title="Yofi Fraud Risk Integration">
                      <YofiIntegrationCard input={input.inputs.yofi} />
                    </Card>
                  </div>
                  <div className={gridCss.span12}>
                    <ReturnProcessingCard input={returnProcessing} />
                  </div>
                  <div className={gridCss.span12}>
                    <ShippingCard
                      input={shipping}
                      labelAdjustmentDefault={
                        input.value.returnsCoverage.merchantPricePerOrder ||
                        input.value.returnsCoverage.pricePerOrder
                      }
                    />
                  </div>
                  <div className={gridCss.span12}>
                    <Card title="Final Sale Returns">
                      <FinalSaleReturnsCard input={finalSaleReturns} />
                    </Card>
                  </div>
                  <div className={gridCss.span12}>
                    <AdditionalCoverageWebSourcesCard
                      input={additionalCoverageWebSources}
                    />
                  </div>
                  <div className={gridCss.span6}>
                    <Card title="AI Exchange Billing Settings">
                      <ExchangeAgentBillingSettingsCard
                        input={exchangeAgentBillingSettings}
                      />
                    </Card>
                  </div>
                  <div className={gridCss.span6}>
                    <PerReturnBillingSettingsCard
                      input={perReturnBillingSettings}
                    />
                  </div>
                  <div className={gridCss.span12}>
                    <SubscriptionBillingCard
                      description="Configure subscription billing settings for returns"
                      input={subscriptionBilling}
                    />
                  </div>
                  {coverageMonitor && (
                    <div className={gridCss.span12}>
                      <CoverageMonitoringCard
                        coverageMonitor={coverageMonitor}
                        onButtonClick={() =>
                          handleCoverageMonitoringReset(id, client)
                        }
                      />
                    </div>
                  )}
                  <div className={gridCss.span12}>
                    <CopyLatestReturnFlowCard sourceTeamId={id} />
                  </div>

                  <div className={gridCss.span12}>
                    <ReturnBearMarkupSettings />
                  </div>

                  {!billingType.value.subscriptionEnabled &&
                  ris?.input?.value.coverage ===
                    CoverageProductEnum.SHIPPING ? (
                    <div className={gridCss.span12}>
                      <ShippingZones
                        hasCarrierServices={hasCarrierServices}
                        hasReturnsCoverage={returnsCoverageIsEnabled(
                          returnsCoverage.value.coverage,
                          returnsGeneral.value.enabled,
                        )}
                        input={ris}
                        packageProtectionInput={packageProtection}
                        teamId={id}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <LabelRatesCard input={returnLabels} />
          </PageForm>
        )}
      </div>
    );
  },
);

async function handleCoverageMonitoringReset(teamId: any, client: any) {
  await resetCoverageMonitoring(client, { teamId });
}

const returnsPackageProtectionFormValueToTeam = ({
  formValue,
  prevTeamValue,
}: {
  formValue: ReturnsPackageProtectionValue;
  prevTeamValue: Team;
}): Team => {
  const team = cloneDeep(prevTeamValue);

  team.settings.canEditReturnAddress = formValue.shipping.canEditReturnAddress;
  team.settings.checkboxText = formValue.storefront.checkbox.text || undefined;
  team.settings.html = formValue.storefront.checkbox.html || undefined;
  team.settings.redoAutoCheck = formValue.storefront.checkbox.default;
  team.settings.checkboxSelector =
    formValue.storefront.checkbox.selector || undefined;
  team.settings.checkboxTextAfterRedo =
    formValue.storefront.checkbox.textAfterRedo || undefined;
  team.settings.hideCheckboxBranding =
    formValue.storefront.checkbox.hideCheckboxBranding;
  team.settings.hideCheckboxPrice =
    formValue.storefront.checkbox.hideCheckboxPrice;
  const newMerchantPaidEnabledDates =
    formValue.returnsCoverage.merchantPaidEnabledDates.map((value) => ({
      start: value.start?.toISOString() || null,
      end: value.end?.toISOString() || null,
    }));
  team.settings.useShadowRootInCart =
    formValue.storefront.cartToggle.useShadowRootInCart;
  team.settings.cartShadowRootSelector =
    formValue.storefront.cartToggle.cartShadowRootSelector;
  team.settings.cartToggleEnabled =
    isAttachmentStrategyUsingCartToggles[
      formValue.storefront.attachmentStrategy
    ];
  team.settings.cartToggleSelector =
    formValue.storefront.cartToggle.selector || undefined;
  team.settings.cartTogglePlacement =
    formValue.storefront.cartToggle.placement || undefined;
  team.settings.cartClickListenerSelector =
    formValue.storefront.cartToggle.clickSelector || undefined;
  team.settings.toggleOnColor =
    formValue.storefront.cartToggle.toggleOnColor || "";
  team.settings.toggleOffColor =
    formValue.storefront.cartToggle.toggleOffColor || "";
  team.settings.toggleCircleColor =
    formValue.storefront.cartToggle.toggleCircleColor || "";
  team.settings.cartReplaceItems =
    formValue.storefront.cartToggle.cartReplaceItems;
  team.settings.productName =
    formValue.storefront.checkbox.productName || undefined;
  team.settings.merchantPaidEnabledDates = newMerchantPaidEnabledDates;
  team.settings.paidModel =
    formValue?.returnsCoverage.merchantPaidEnabledDates?.some(
      (dateRange) => dateRange.end == null,
    )
      ? PaymentModel.MERCHANT_PAID
      : PaymentModel.CUSTOMER_PAID;
  team.settings.subscriptionEnabled = formValue.billingType.subscriptionEnabled;
  team.settings.coverage.exchange = formValue.returnsCoverage.coverage.exchange;
  team.settings.coverage.refund = formValue.returnsCoverage.coverage.refund;
  team.settings.coverage.storeCredit =
    formValue.returnsCoverage.coverage.credit;
  team.settings.coverageEnabled = returnsCoverageIsEnabled(
    formValue.returnsCoverage.coverage,
    formValue.returnsGeneral.enabled,
  );

  team.settings.excludeCoverageIfAnyInvalidProduct =
    formValue.returnsCoverage.excludeCoverageIfAnyInvalidProduct;
  team.settings.pickupEnabled = formValue.shipping.pickupEnabled;
  team.settings.pickupEnabledForHeavyItems =
    formValue.shipping.pickupEnabledForHeavyItems;
  team.settings.pickupMaxLabelUpgradeRange = formValue.shipping
    .pickupMaxLabelUpgradeRange
    ? +formValue.shipping.pickupMaxLabelUpgradeRange
    : undefined;
  team.settings.shippingCarrierOverride = formValue.shipping.carrierOverride;
  team.settings.shippingInsurance = formValue.shipping.insured
    ? ShippingInsurance.ENABLED
    : ShippingInsurance.DISABLED;
  team.settings.labelPriceAdjustment =
    +formValue.shipping.labelAdjustment || null;
  team.settings.exchanges = {
    ...team.settings.exchanges,
    exchangeOptionsPreviewShowOriginalPrice:
      formValue.exchanges.exchangeOptionsPreviewShowOriginalPrice,
    shopSite: {
      ...team.settings.exchanges.shopSite,
      navigationMode: formValue.exchanges.shopSiteNewTab
        ? ShopSiteNavigationMode.NEW_TAB
        : ShopSiteNavigationMode.SAME_TAB,
    },
  };
  team.settings.generateQrCodes = formValue.shipping.generateQrCodes;
  team.settings.shopAgainAction = formValue.returnProcessing.shopAgain;
  team.settings.riseTrigger = formValue.returnProcessing.riseTriggerUrl
    ? { url: formValue.returnProcessing.riseTriggerUrl }
    : null;
  team.settings.portalExcludedProductTags = formValue.returnProcessing
    .portalExcludedProductTags as string[];
  team.settings.portalExcludedCollections = formValue.returnProcessing
    .portalExcludedCollections as string[];
  team.settings.portalExcludedProductIds = formValue.returnProcessing
    .portalExcludedProductIds as string[];
  team.settings.protectionExcludeTags = formValue.packageProtection
    .protectionExcludeTags as string[];
  team.settings.packageProtection = {
    ...team.settings.packageProtection,
    ...packageProtectionFormValueToTeam(
      formValue.packageProtection,
      formValue.storefront.splitProducts,
    ),
  };
  team.settings.yofi = yofiFormValueToTeam(formValue.yofi);
  team.settings.customerPortalVersion =
    formValue.returnsGeneral.customerPortalVersion;
  if (
    !formValue.returnsGeneral.enabled ||
    !formValue.packageProtection.enabled
  ) {
    team.settings.packageProtection.splitProducts = false;
  }
  team.settings.bulkReturnsEnabled =
    formValue.returnsGeneral.bulkReturnsEnabled;
  team.settings.inventory.allowRestockBeforeProcess =
    formValue.returnProcessing.allowRestockBeforeProcess;

  team.settings.returns = {
    ...team.settings.returns,
    enabled: formValue.returnsGeneral.enabled,
    refundAllReturnedItems: formValue.returnsGeneral.refundAllReturnedItems,
    skipExchangeRefunds: formValue.returnsGeneral.skipExchangeRefunds,
    settlementEnabled: formValue.returnsGeneral.settlementEnabled,
    allowMultiOrderReturns: formValue.returnsGeneral.allowMultiOrderReturns,
    hideGiftCardBranding: formValue.returnProcessing.hideGiftCardBranding,
    multiOrderReturnLimit: Number(
      formValue.returnsGeneral.multiOrderReturnLimit,
    ),
    settlementRefund: Number(formValue.returnsGeneral.settlementRefund) / 100,
    internationalPricing: formValue.returnsCoverage.internationalPricing.map(
      (price) => {
        return {
          countries: price.countries,
          multiplier: Math.floor(Number(price.multiplier) * 100) / 100,
        };
      },
    ),
    disableRejectedItems: formValue.returnsGeneral.disableRejectedItems,
    checkForTryNowTags: formValue.returnProcessing.checkForTryNowTags,
    enableCustomerPortal: formValue.returnsGeneral.enableCustomerPortal,
    enableShopAppReturns: formValue.returnsGeneral.enableShopAppReturns,
    buyYourOwnLabel: formValue.returnsGeneral.buyYourOwnLabel,
    finalSaleReturns: finalSaleReturnsFormValueToTeam(
      formValue.finalSaleReturns,
    ),
    dontChargeMerchantForDamagedOrWrongProductReturnLabels:
      !formValue.returnsGeneral
        .chargeMerchantForDamagedOrWrongProductReturnLabels,
    merchantCarrierReturnLabels:
      formValue.returnLabels.merchantCarrierReturnLabels,
    agentReviewEnabled: formValue.returnsGeneral.agentReviewEnabled,
    agentChatEnabled: formValue.returnsGeneral.agentChatEnabled,
    subscriptionBilling: subscriptionBillingFormValueToTeam(
      formValue.subscriptionBilling,
    ),
  };
  team.settings.exchanges.nonZeroValueExchangeStrategy =
    formValue.returnsGeneral.nonZeroValueExchangeStrategy;
  team.shopifyCoverageProductSource = formValue.storefront.shopifyProductSource;

  const dynamicPricingRuleSets = pricingRulesConvertValueToTeam(
    formValue.returnsCoverage.dynamicPriceCartValueRuleSet,
  )?.[0];

  const pricingType = formValue.returnsCoverage.returnPricingSelection;

  if (
    pricingType === ReturnsPricingEnum.PER_ITEM &&
    formValue.returnsCoverage.pricePerAdditonalItem &&
    formValue.returnsCoverage.maxPrice
  ) {
    team.settings.returns.dynamicPricing = {
      type: DynamicReturnTypes.ITEM_COUNT,
      pricePerAdditonalItem: Number(
        formValue.returnsCoverage.pricePerAdditonalItem,
      ),
      maxPrice: Number(formValue.returnsCoverage.maxPrice),
    };
  } else if (
    pricingType === ReturnsPricingEnum.ORDER_TOTAL &&
    dynamicPricingRuleSets
  ) {
    team.settings.returns.dynamicPricing = {
      type: DynamicReturnTypes.CART_VALUE,
      maxPrice: Number(formValue.returnsCoverage.maxPrice),
      pricingRuleSet: dynamicPricingRuleSets,
    };
  } else {
    team.settings.returns.dynamicPricing = undefined;
  }

  team.settings.countries = formValue.returnsCoverage.serviceCountries;
  team.settings.countriesPackageProtection =
    formValue.packageProtection.pricingRuleSet[0].countries ?? [];
  team.merchantPricePerOrder =
    formValue.returnsCoverage.merchantPricePerOrder || undefined;
  team.pricePerOrder = formValue.returnsCoverage.pricePerOrder;
  team.settings.returns.pricePerOrder = formValue.returnsCoverage.pricePerOrder;
  team.settings.returnsSKU =
    formValue.storefront.returnProductOptions.sku || "";
  team.settings.packageProtectionSKU =
    formValue.storefront.PackageProtectionProductOptions.sku || "";
  team.settings.combinedSKU = formValue.storefront.BothProductOptions.sku || "";
  team.settings.uniqueVariantSKU =
    formValue.storefront.uniqueVariantSKU ?? false;
  team.settings.indexBasedUniqueVariantStartingAt =
    formValue.storefront.indexBasedUniqueVariantStartingAt;

  team.settings.shopifyExtension = {
    ...team.settings.shopifyExtension,
    customizationVisible:
      team.settings.shopifyExtension?.customizationVisible ?? false,
    customizeCartItem:
      team.settings.shopifyExtension?.customizeCartItem ?? false,
    brandPrefix: team.settings.shopifyExtension?.brandPrefix ?? "",
    bothProductNameOverride:
      formValue.storefront.BothProductOptions.productTitle ?? "",
    packageProtectionNameOverride:
      formValue.storefront.PackageProtectionProductOptions.productTitle ?? "",
    returnNameOverride:
      formValue.storefront.returnProductOptions.productTitle ?? "",
    redoVariantBaseName: formValue.storefront.redoVariantBaseName ?? "",
    toggleTextOptions: {
      returnToggle: {
        titleText:
          formValue.storefront.returnProductOptions.toggleMainText ?? "",
        positiveSubtext:
          formValue.storefront.returnProductOptions.toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.returnProductOptions.toggleSubTextNegative ?? "",
      },
      packageProtectionToggle: {
        titleText:
          formValue.storefront.PackageProtectionProductOptions.toggleMainText ??
          "",
        positiveSubtext:
          formValue.storefront.PackageProtectionProductOptions
            .toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.PackageProtectionProductOptions
            .toggleSubTextNegative ?? "",
      },
      bothProductToggle: {
        titleText: formValue.storefront.BothProductOptions.toggleMainText ?? "",
        positiveSubtext:
          formValue.storefront.BothProductOptions.toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.BothProductOptions.toggleSubTextNegative ?? "",
      },
    },
    attachmentStrategy: formValue.storefront.attachmentStrategy,
    toggleSubtextEnabled: formValue.storefront.toggleSubtextEnabled,
    hideRedoProduct: {
      enabled: formValue.storefront.hideRedoProduct.enabled,
      productSelector: formValue.storefront.hideRedoProduct.productSelector,
      checkoutButtonSelector:
        team.settings.shopifyExtension?.hideRedoProduct
          ?.checkoutButtonSelector ?? "",
      cartCountModifications:
        formValue.storefront.hideRedoProduct.cartCountModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
      cartPriceModifications:
        formValue.storefront.hideRedoProduct.cartPriceModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
      cartDiscountModifications:
        formValue.storefront.hideRedoProduct.cartDiscountModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
    },
    checkoutExtensionEnabled:
      isAttachmentStrategyCheckoutExtensionEnabled[
        formValue.storefront.attachmentStrategy
      ],
    customPdpCss: formValue.storefront.customPdpCss,
    customToggleCss: formValue.storefront.customToggleCss,
    usingCartAndCheckoutToggle: formValue.storefront.usingCartAndCheckoutToggle,
    autoNavigateToCheckoutAfterTimeout:
      formValue.storefront.autoNavigateToCheckoutAfterTimeout,
    trackRedoProductInventory: formValue.storefront.trackRedoProductInventory,
    coverageCreditIncentive: {
      enabled: formValue.returnsCoverage.coverageCreditIncentive.enabled,
      type: formValue.returnsCoverage.coverageCreditIncentive.type,
      fixedAmount:
        parseFloat(
          formValue.returnsCoverage.coverageCreditIncentive.fixedAmount,
        ) ?? 5,
      percentageSettings: {
        percentage:
          parseFloat(
            formValue.returnsCoverage.coverageCreditIncentive.percentageSettings
              .percentage,
          ) || 5,
        storeCreditAmountCap: formValue.returnsCoverage.coverageCreditIncentive
          .percentageSettings.storeCreditAmountCap
          ? parseFloat(
              formValue.returnsCoverage.coverageCreditIncentive
                .percentageSettings.storeCreditAmountCap,
            )
          : null,
      },
      minimumQualifyingPurchaseAmount:
        parseFloat(
          formValue.returnsCoverage.coverageCreditIncentive
            .minimumQualifyingPurchaseAmount,
        ) || 0,
      creditExpirationDays:
        parseInt(
          formValue.returnsCoverage.coverageCreditIncentive
            .creditExpirationDays,
        ) || null,
      sendEmail: formValue.returnsCoverage.coverageCreditIncentive.sendEmail,
    },
  };
  team.settings.packageProtectionAutoCheck =
    formValue.storefront.packageProtectionAutoCheck;
  team.settings.modalTextAdjustments = {
    text1: formValue.storefront.checkboxInfoModal.text1,
    title1: formValue.storefront.checkboxInfoModal.title1,
    text2: formValue.storefront.checkboxInfoModal.text2,
    title2: formValue.storefront.checkboxInfoModal.title2,
    text3: formValue.storefront.checkboxInfoModal.text3,
    title3: formValue.storefront.checkboxInfoModal.title3,
  };
  team.settings.infoModal = {
    ...team.settings.infoModal,
    cartIconEnabled: formValue.storefront.checkboxInfoModal.cartIconEnabled,
    checkoutIconEnabled:
      formValue.storefront.checkboxInfoModal.checkoutIconEnabled,
    version: formValue.storefront.checkboxInfoModal.version,
    logo: formValue.storefront.checkboxInfoModal.newLogo
      ? formValue.storefront.checkboxInfoModal.newLogo.toString()
      : "",
    sideImage: formValue.storefront.checkboxInfoModal.sideImage
      ? formValue.storefront.checkboxInfoModal.sideImage.toString()
      : "",
  };
  team.settings.modalLogo = formValue.storefront.checkboxInfoModal.logo
    ? formValue.storefront.checkboxInfoModal.logo.toString()
    : "";

  team.settings.exchanges.selection = formValue.exchanges.selection;
  team.settings.checkoutSelector =
    formValue.exchanges.checkoutSelector === ""
      ? undefined
      : formValue.exchanges.checkoutSelector;
  team.settings.exchangeBannerColor = formValue.exchanges.exchangeBannerColor;
  team.settings.exchangeBannerText = formValue.exchanges.exchangeBannerText;
  team.settings.shopifyExtension!.exchangeBannerPositionBottom =
    formValue.exchanges.exchangeBannerPositionBottom;
  team.settings.shopifyExtension!.exchangeBannerInsertBeforeBody =
    formValue.exchanges.exchangeBannerInsertBeforeBody;
  team.settings.exchangeBannerFont = formValue.exchanges.exchangeBannerFont;
  team.settings.exchangeBannerFontColor =
    formValue.exchanges.exchangeBannerFontColor;
  team.settings.exchangeBannerShowDoneButton =
    formValue.exchanges.exchangeBannerShowDoneButton;
  team.settings.exchangeBannerCustomCss =
    formValue.exchanges.exchangeBannerCustomCss;

  team.settings.returns.removeMerchantPaidLabelUpcharge =
    formValue.returnLabels.disableMerchantPaidUpcharge;
  team.settings.returns.merchantCarrierReturnLabels =
    formValue.returnLabels.merchantCarrierReturnLabels;
  team.settings.returns.returnLabelQuantity =
    formValue.shipping.returnLabelQuantity;
  team.settings.returns.useFixedPackageDimensions =
    formValue.shipping.useFixedPackageDimensions;
  team.settings.returns.fixedPackageDimensions = formValue.shipping
    .useFixedPackageDimensions
    ? {
        length:
          Number(formValue.shipping.fixedPackageDimensions.length) || undefined,
        width:
          Number(formValue.shipping.fixedPackageDimensions.width) || undefined,
        height:
          Number(formValue.shipping.fixedPackageDimensions.height) || undefined,
        weight:
          Number(formValue.shipping.fixedPackageDimensions.weight) || undefined,
      }
    : undefined;
  team.settings.returns.refundExpiredLabels =
    formValue.shipping.refundExpiredLabels;

  // Update additional coverage web sources
  team.settings.additionalCoverageWebSources = [
    ...formValue.additionalCoverageWebSources,
  ];

  // Exchange Agent billing settings
  const updatedExchangeAgentBillingSettings =
    getUpdatedExchangeAgentBillingSettings(
      team.settings.returnsAiExchange,
      formValue.exchangeAgentBillingSettings,
    );
  team.settings.returnsAiExchange.enabled =
    updatedExchangeAgentBillingSettings.enabled;
  team.settings.returnsAiExchange.enableBilling =
    updatedExchangeAgentBillingSettings.enableBilling;
  team.settings.returnsAiExchange.renewOnPeriodEnd =
    updatedExchangeAgentBillingSettings.renewOnPeriodEnd;
  team.settings.returnsAiExchange.usageFee =
    updatedExchangeAgentBillingSettings.usageFee;
  // Only worry about updating the start date if we are enabling billing.
  if (
    team.settings.returnsAiExchange.enableBilling &&
    team.settings.returnsAiExchange.renewOnPeriodEnd
  ) {
    team.settings.returnsAiExchange.startDate =
      updatedExchangeAgentBillingSettings.startDate;
  }

  // Update per-return billing settings
  if (!team.settings.perReturnBilling) {
    team.settings.perReturnBilling = {
      enabled: false,
      usageFee: DEFAULT_PER_RETURN_BILLING_FEE,
    };
  }

  team.settings.perReturnBilling.enabled =
    formValue.perReturnBillingSettings.enabled;
  team.settings.perReturnBilling.usageFee = isNaN(
    Number(formValue.perReturnBillingSettings.usageFee),
  )
    ? DEFAULT_PER_RETURN_BILLING_FEE
    : Number(formValue.perReturnBillingSettings.usageFee);

  return team;
};
